const createElementFromHTML = (htmlString) => {
    const div = document.createElement('div');
    div.innerHTML = htmlString.trim();

    if (div.children.length > 1) {
        const itemsArray = [];
        Array.prototype.push.apply(itemsArray, div.children);

        return itemsArray;
    } else {
        return div.firstChild;
    }
};

const getOffset = (el) => {
    const rect = el.getBoundingClientRect();
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    return {
        top: rect.top + scrollTop,
        left: rect.left + scrollLeft,
        bottom: rect.top + scrollTop + rect.height
    };
};

const loadScript = (source, beforeEl, async = true, defer = true) => {
    return new Promise((resolve, reject) => {
        let script = document.createElement('script');
        const prior = beforeEl || document.getElementsByTagName('script')[0];

        script.async = async;
        script.defer = defer;

        function onloadHander (_, isAbort) {
            if (isAbort || !script.readyState || /loaded|complete/.test(script.readyState)) {
                script.onload = null;
                script.onreadystatechange = null;
                script = undefined;

                if (isAbort) {
                    const error = new Error('something went wrong');
                    reject(error);
                } else {
                    resolve(script);
                }
            }
        }

        script.onload = onloadHander;
        script.onreadystatechange = onloadHander;

        script.src = source;
        prior.parentNode.insertBefore(script, prior);
    });
};

const smoothScrollTo = (endX, endY, duration) => {

    let startX = window.scrollX || window.pageXOffset,
        startY = window.scrollY || window.pageYOffset,
        distanceX = endX - startX,
        distanceY = endY - startY,
        startTime = new Date().getTime();

    duration = typeof duration !== 'undefined' ? duration : 400;

    // Easing function
    const easeInOutQuart = (time, from, distance, duration) => {
        if ((time /= duration / 2) < 1) return distance / 2 * time * time * time * time + from;
        return -distance / 2 * ((time -= 2) * time * time * time - 2) + from;
    };

    const timer = window.setInterval(() => {
        let time = new Date().getTime() - startTime,
            newX = easeInOutQuart(time, startX, distanceX, duration),
            newY = easeInOutQuart(time, startY, distanceY, duration);
        if (time >= duration) {
            window.clearInterval(timer);
        }
        window.scrollTo(newX, newY);
    }, 1000 / 60); // 60 fps
};

const tryParseJSON = (jsonString) => {
    try {
        let o = JSON.parse(jsonString);

        // Handle non-exception-throwing cases:
        // Neither JSON.parse(false) or JSON.parse(1234) throw errors, hence the type-checking,
        // but... JSON.parse(null) returns null, and typeof null === "object",
        // so we must check for that, too. Thankfully, null is falsey, so this suffices:
        if (o && typeof o === 'object') {
            return o;
        }
    }
    catch (e) {
        // Fallback logging consistent with guidelines (no undefined globals)
        // Use console.error in development; avoid throwing to keep function safe
        if (typeof console !== 'undefined' && typeof console.error === 'function') {
            console.error(e);
        }
    }

    return false;
};

const loadYoutubeApi = () => {
    if (typeof YT === 'undefined') {
        const tag = document.createElement('script');
        tag.src = '//www.youtube.com/iframe_api';

        const firstScriptTag = document.getElementsByTagName('script')[0];
        firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
    }
};

const loadStyle = (src) => {
    return new Promise((resolve, reject) => {
        // Normalize src to a trimmed string early for robust handling
        const normalizedSrc = (src != null ? String(src) : '').trim();

        if (!normalizedSrc) {
            reject(new Error(`Style load error: empty or invalid src (${Object.prototype.toString.call(src)})`));
            return;
        }

        const existingLink = Array.from(document.getElementsByTagName('link')).find(l => l.href && l.href.includes(normalizedSrc));
        if (existingLink) {
            resolve(existingLink);
            return;
        }

        const link = document.createElement('link');
        link.href = normalizedSrc;
        link.rel = 'stylesheet';

        link.onload = () => resolve(link);
        link.onerror = () => reject(new Error(`Style load error for ${normalizedSrc}`));

        document.head.append(link);
    });
};

const loadFiles = (dataType, sources) => {
    const promises = [];

    // Normalize sources into a flat array of unique, non-empty strings
    let list;
    if (sources == null) {
        list = [];
    } else if (typeof sources[Symbol.iterator] === 'function' && typeof sources !== 'string') {
        // Covers Array, Set, NodeList, etc.
        list = Array.from(sources);
    } else {
        list = [sources];
    }

    // Flatten one level for nested iterables like [Set([...])]
    const flattened = [];
    list.forEach((item) => {
        if (item && typeof item !== 'string' && typeof item[Symbol.iterator] === 'function' && !(item instanceof Element)) {
            Array.from(item).forEach((v) => flattened.push(v));
        } else {
            flattened.push(item);
        }
    });

    const unique = Array.from(new Set(flattened
        .map((v) => (v != null ? String(v) : ''))
        .map((s) => s.trim())
        .filter(Boolean)));

    unique.forEach((src) => {
        if (dataType === 'css') {
            promises.push(loadStyle(src));
        } else if (dataType === 'js') {
            promises.push(loadScript(src, document.getElementsByTagName('script')[0]));
        }
    });

    return Promise.all(promises);
};

const checkAndLoadFiles = (content) => {
    const promises = [];

    // Helper to collect nodes that have the data attribute, including the root element itself
    const collectNodes = (type) => {
        const selector = `[data-${type}]`;
        const nodes = Array.from(content.querySelectorAll(selector));
        if (content instanceof Element && content.matches && content.matches(selector)) {
            nodes.push(content);
        }
        return nodes;
    };

    const dataSources = new Map([
        ['css', collectNodes('css')],
        ['js', collectNodes('js')]
    ]);

    for (const [type, nodes] of dataSources) {
        const urlArray = [];

        if (nodes.length > 0) {
            nodes.forEach((item) => {
                const raw = item.getAttribute(`data-${type}`);
                if (typeof raw !== 'string') return;
                const urlData = raw.trim();
                if (!urlData) return;

                if (urlData.indexOf(',') === -1) {
                    urlArray.push(urlData);
                } else {
                    urlData.split(',').forEach((urlString) => {
                        const cleaned = (urlString || '').trim();
                        if (cleaned) urlArray.push(cleaned);
                    });
                }
            });

            if (urlArray.length > 0) {
                const uniqueSet = new Set(urlArray);
                const filteredUrls = [...uniqueSet];
                promises.push(loadFiles(type, filteredUrls));
            }
        }
    }

    return Promise.all(promises);
};

const focusFirstInteractive = ($context) => {
    // Only auto-focus when the user is navigating via keyboard.
    if (!(window.inputModality && window.inputModality.isKeyboard)) return;
    const ctx = $context || this.$form || this.$root;
    if (!ctx) return;

    // Common focusable selectors
    const selectors = [
        'input:not([type="hidden"]):not([disabled])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        'button:not([disabled])',
        'a[href]',
        '[tabindex]:not([tabindex="-1"])'
    ].join(',');

    const tryFocus = () => {
        const $focusables = Array.from(ctx.querySelectorAll(selectors))
            .filter(el => {
                // skip elements that are not actually visible
                const style = window.getComputedStyle(el);
                const isHidden = style.display === 'none' || style.visibility === 'hidden';
                const disabled = el.hasAttribute('disabled');
                return !isHidden && !disabled;
            });

        const $first = $focusables[0];
        if ($first && typeof $first.focus === 'function') {
            // If it's a radio group, focus the first radio input
            $first.focus({preventScroll: true});
        }
    };

    // Defer to next frame(s) to ensure DOM is painted and any CSS-driven
    // visibility (e.g. transitions based on --visible-index) is applied.
    requestAnimationFrame(() => requestAnimationFrame(tryFocus));
};

export {
    createElementFromHTML,
    getOffset,
    loadScript,
    smoothScrollTo,
    tryParseJSON,
    loadYoutubeApi,
    loadStyle,
    loadFiles,
    checkAndLoadFiles,
    focusFirstInteractive
};
